// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import React, { createContext, useContext } from 'react';
import { FileUploadManager } from '../file-sharing';
import { FileUploadAdapter } from './AzureCommunicationFileUploadAdapter';
import { ChatAdapter } from './ChatAdapter';

/**
 * @private
 */
type ChatProviderProps = {
  children: React.ReactNode;
  adapter: ChatAdapter;
};
const ChatAdapterContext = createContext<ChatAdapter | undefined>(undefined);

/**
 * @private
 */
export const ChatAdapterProvider = (props: ChatProviderProps): JSX.Element => {
  const {
    adapter
  } = props;
  return <ChatAdapterContext.Provider value={adapter}>{props.children}</ChatAdapterContext.Provider>;
};

/**
 * @private
 */
export const useAdapter = (): ChatAdapter => {
  const adapter = useContext(ChatAdapterContext);
  if (!adapter) {
    throw 'Cannot find adapter please initialize before usage.';
  }
  return adapter;
};

/**
 * @private
 */
export const useFileUploadAdapter = (): FileUploadAdapter => {
  // A stub that short-circuits all logic because none of the fields are available.
  return {
    registerActiveFileUploads() {
      return ([] as FileUploadManager[]);
    },
    registerCompletedFileUploads() {
      return ([] as FileUploadManager[]);
    },
    cancelFileUpload() {
      // noop
    },
    clearFileUploads() {
      // noop
    },
    updateFileUploadErrorMessage() {
      // noop
    },
    updateFileUploadProgress() {
      // noop
    },
    updateFileUploadMetadata() {
      // noop
    }
  };
};